// Onboarding Wizard — 3-step flow
//   Step 1: WelcomeStep
//   Step 2: WorkspaceCreationStep (join invite OR create new workspace)
//   Step 3: IntroductionStep (boost policy or skip to dashboard)

import { AlertCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@foundation/contextapi/UserContext';
import { useToast } from '@foundation/hooks/use-toast';
import { supabase } from '@foundation/utils/supabaseClient';
import { completeOnboarding } from '@foundation/utils/teableOnboarding';
import { Card, CardContent } from '@foundation/components/ui/card';
import { WorkspaceSetupLoading } from '@foundation/components/ui/LoadingStates';
import { Progress } from '@foundation/components/ui/progress';
import {
  getOnboardingConfig,
  type OnboardingMode,
} from '@foundation/services/inviteService';
import WelcomeStep from './steps/WelcomeStep';
import WorkspaceCreationStep from './steps/WorkspaceCreationStep';
import { IntroductionStep } from '@/features/revisr/components/sample-policy/steps/IntroductionStep';

export interface OnboardingStepProps {
  organizationId?: string;
  userId: string;
  onNext: (data: Record<string, unknown>) => void;
  onSkip?: () => void;
  onBack?: () => void;
  inviteType?: 'personal' | 'organisation' | null;
  pendingInviteCode?: string;
  pendingTenantId?: string;
  onboardingMode?: OnboardingMode;
  isPlatformAdmin?: boolean;
  /** role_code of the invite the user arrived with (e.g. 'manager' | 'member') */
  inviteRoleCode?: string | null;
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<OnboardingStepProps>;
  required: boolean;
  order: number;
}

interface OnboardingData {
  workspaceName?: string;
  workspaceDescription?: string;
  workspaceType?: string;
  profileData?: Record<string, unknown>;
}

// Adapts IntroductionStep (sample-policy) to the OnboardingStepProps interface.
// onNext  → "Boost Your First Policy" → navigate to /sample-policy (handled by wizard)
// onSkip  → "Skip and go to dashboard" → passed in from wizard
const OnboardingIntroductionStep: React.FC<OnboardingStepProps> = ({ onNext, onSkip }) => (
  <IntroductionStep onNext={() => onNext({})} onSkip={onSkip} />
);

interface OnboardingWizardProps {
  onComplete?: () => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUpWorkspace, setIsSettingUpWorkspace] = useState(false);
  const [workspaceSetupStatus, setWorkspaceSetupStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteType, setInviteType] = useState<'personal' | 'organisation' | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);
  const [onboardingMode, setOnboardingMode] = useState<OnboardingMode>('allow_all');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [inviteRoleCode, setInviteRoleCode] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      const mode = await getOnboardingConfig();
      setOnboardingMode(mode);

      if (user?.id) {
        const { data: profile } = await supabase
          .from('base_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        if ((profile as { role?: string } | null)?.role === 'admin') {
          setIsPlatformAdmin(true);
        }
      }

      const searchParams = new URLSearchParams(location.search);
      const inviteCodeFromUrl = searchParams.get('invite');

      if (inviteCodeFromUrl) {
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
          const response = await fetch(`${apiUrl}/api/invites/validate/${inviteCodeFromUrl}`);
          if (response.ok) {
            const data = await response.json();
            setInviteType(data.invite_type);
            setInviteCode(inviteCodeFromUrl);
            setTenantId(data.tenant_id);
            setInviteRoleCode(data.role_code ?? null);
          }
        } catch (err) {
          console.error('Error validating invite from URL:', err);
        }
      }
    };

    init();
  }, [user, location.search]);

  const coreSteps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Revisr',
      description: 'Discover what Revisr can do for your team',
      component: WelcomeStep,
      required: true,
      order: 1,
    },
    {
      id: 'workspace',
      title: 'Setup Organisation',
      description: 'Join or create your organisation',
      component: WorkspaceCreationStep,
      required: true,
      order: 2,
    },
    {
      id: 'introduction',
      title: 'Get Started',
      description: 'Ready to boost your first policy',
      component: OnboardingIntroductionStep,
      required: false,
      order: 3,
    },
  ];

  const allSteps = coreSteps;
  const totalSteps = allSteps.length;
  const currentStepId = allSteps[currentStep - 1]?.id;

  // Safe session write: try update first; insert only if no row exists yet.
  // Avoids relying on a UNIQUE constraint on user_id.
  const writeSession = async (payload: {
    current_step: number;
    completed_steps: number[];
    status: string;
    completed_at?: string;
  }) => {
    if (!user?.id) return;
    const { data: updated } = await supabase
      .from('base_onboarding_sessions')
      .update(payload)
      .eq('user_id', user.id)
      .select('session_id');

    if (!updated || updated.length === 0) {
      await supabase
        .from('base_onboarding_sessions')
        .insert({ user_id: user.id, ...payload });
    }
  };

  // Advance to the Introduction step (step 3) and record session progress.
  // is_onboarded is NOT set here — it is set only when step 3 is completed.
  const markOnboardedAndAdvance = async () => {
    if (!user?.id) return;
    try {
      await writeSession({ current_step: 3, completed_steps: [1, 2], status: 'in_progress' });
    } catch (err) {
      console.error('Failed to update onboarding session:', err);
    }
    setCurrentStep(3);
  };

  const handleNext = async (stepData: Record<string, unknown>) => {
    const mergedData = { ...onboardingData, ...stepData };
    setOnboardingData(mergedData);

    // ── Welcome step → record step 1 completion and advance ────────────────
    if (currentStepId === 'welcome') {
      try {
        await writeSession({ current_step: 2, completed_steps: [1], status: 'in_progress' });
      } catch (err) {
        console.error('Failed to update onboarding session:', err);
      }
      setCurrentStep(2);
      return;
    }

    // ── Workspace step ──────────────────────────────────────────────────────
    if (currentStepId === 'workspace') {
      // User accepted an invite (joined existing org)
      if (stepData.hasAcceptedInvites === true || stepData.joinedWorkspace === true) {
        setIsLoading(true);
        setError(null);
        try {
          const roleLabels: Record<string, string> = {
            admin: 'Platform Admin',
            org_admin: 'Organisation Admin',
            internal_user: 'Internal User',
            consultant: 'Consultant',
            auditor: 'Auditor',
            member: 'Member',
          };
          const role = (stepData.role as string) || '';
          toast({
            title: 'Welcome to Revisr!',
            description: role
              ? `You have joined the organisation successfully as ${roleLabels[role] ?? role}.`
              : 'You have joined the organisation successfully.',
          });
          await markOnboardedAndAdvance();
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Workspace already created in a prior attempt — just advance
      if (createdTenantId) {
        setCurrentStep(prev => prev + 1);
        return;
      }

      // Create new workspace
      setIsLoading(true);
      setError(null);
      try {
        setIsSettingUpWorkspace(true);
        setWorkspaceSetupStatus('Creating organisation and setting up tables...');

        const result = await completeOnboarding({
          workspaceName: mergedData.workspaceName!,
          workspaceDescription: mergedData.workspaceDescription,
          workspaceType: mergedData.workspaceType ?? 'organisation',
          profileData: mergedData.profileData ?? {},
        });

        if (!result.success || !result.tenantId) {
          throw new Error(result.error ?? 'Organisation creation failed');
        }

        const newTenantId = result.tenantId;
        setCreatedTenantId(newTenantId);
        setWorkspaceSetupStatus('Organisation setup complete!');
        setIsSettingUpWorkspace(false);

        // Store default branding (fire-and-forget)
        supabase
          .from('base_organisation_branding')
          .upsert(
            {
              organisation_id: newTenantId,
              primary_color: '#000000',
              secondary_color: '#000000',
              font_color: '#000000',
              font_family: 'Inter',
              created_by: user?.id,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'organisation_id' }
          )
          .then(({ error: brandErr }) => {
            if (brandErr) console.error('Default branding creation error:', brandErr);
          });

        toast({
          title: 'Organisation Created!',
          description: "Your organisation is ready. Let's get started!",
        });

        await markOnboardedAndAdvance();
      } catch (err) {
        console.error('Workspace creation failed:', err);
        setIsSettingUpWorkspace(false);
        setError(
          err instanceof Error ? err.message : 'Failed to create organisation. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // ── Introduction step ───────────────────────────────────────────────────
    // "Boost Your First Policy" → mark onboarded, record completion, navigate
    if (currentStepId === 'introduction') {
      if (!user?.id) { navigate('/onboarding/boost-policy'); return; }
      setIsLoading(true);
      try {
        await supabase
          .from('base_profiles')
          .update({ is_onboarded: true })
          .eq('id', user.id);

        await writeSession({
          current_step: 3,
          completed_steps: [1, 2, 3],
          completed_at: new Date().toISOString(),
          status: 'completed',
        });

        await queryClient.invalidateQueries({ queryKey: ['user'] });
        await queryClient.invalidateQueries({ queryKey: ['user-onboarding'] });
      } catch (err) {
        console.error('Failed to complete onboarding:', err);
      } finally {
        setIsLoading(false);
      }

      if (onComplete) {
        onComplete();
        setTimeout(() => navigate('/onboarding/boost-policy'), 100);
      } else {
        navigate('/onboarding/boost-policy');
      }
      return;
    }

    // ── Default: advance ────────────────────────────────────────────────────
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  // "Skip and go to dashboard" on Introduction step —
  // marks is_onboarded + sample_policy_completed and records session completion.
  const handleIntroductionSkip = async () => {
    if (!user?.id) { navigate('/dashboard'); return; }

    try {
      await supabase
        .from('base_profiles')
        .update({ is_onboarded: true, sample_policy_completed: true })
        .eq('id', user.id);

      await writeSession({
        current_step: 3,
        completed_steps: [1, 2, 3],
        completed_at: new Date().toISOString(),
        status: 'completed',
      });

      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ['user-onboarding'] });
    } catch (err) {
      console.error('Failed to complete onboarding on skip:', err);
    }

    if (onComplete) {
      onComplete();
      setTimeout(() => navigate('/dashboard'), 100);
    } else {
      navigate('/dashboard');
    }
  };

  const CurrentStepComponent = allSteps[currentStep - 1]?.component;

  if (!CurrentStepComponent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Step</h2>
          <p className="text-gray-600">The onboarding step could not be loaded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {isSettingUpWorkspace && (
        <WorkspaceSetupLoading
          message="Setting up workspace and app..."
          statusMessage={workspaceSetupStatus}
        />
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Progress header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-lg md:text-2xl font-bold text-foreground">
                {allSteps[currentStep - 1]?.title}
              </h1>
              <span className="text-sm text-muted-foreground">
                Step {currentStep} of {totalSteps}
              </span>
            </div>

            <Progress value={(currentStep / totalSteps) * 100} className="mb-4" />

            <p className="text-sm text-muted-foreground">
              {allSteps[currentStep - 1]?.description}
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step content */}
          <Card>
            <CardContent className="pt-6">
              <CurrentStepComponent
                userId={user?.id ?? ''}
                onNext={handleNext}
                onBack={currentStep > 1 ? handleBack : undefined}
                onSkip={currentStepId === 'introduction' ? handleIntroductionSkip : undefined}
                inviteType={inviteType}
                pendingInviteCode={inviteCode ?? undefined}
                pendingTenantId={tenantId ?? undefined}
                onboardingMode={onboardingMode}
                isPlatformAdmin={isPlatformAdmin}
                inviteRoleCode={inviteRoleCode}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
